// 月曆頁的導航（W58.5 從 `routes/Calendar.tsx` 拆出來）。
//
// **為什麼拆。** `routes/Calendar.tsx` 撞到 root `AGENTS.md §3 #10` 的
// **15 KB route 體積上限**，而 W58.5 往裡面加的東西（橫幅順序、主按鈕的
// 事件路徑、日詳情的「標記今日經期開始」）每一件都是**導航**。
//
// 這不是為了過關而搬檔案 —— 這四個動作本來就是**同一件事**：「從月曆頁的
// 某個東西跳到某個地方」，而它們的規則（建立 / 編輯 / 前往來源 / 開一筆
// 週期事件）是同一組。把它們放在一起，路由那邊就只剩版面。
//
// 這是這個檔案裡第三個同樣的拆分（`useCalendarFilters` 拿篩選狀態、
// `useCalendarUndoBar` 拿撤銷條），所以它不是新發明。

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import type { CalendarItem } from "@/lib/api/calendar";
import { LINK_SOURCE_ROUTE, isLinkSourceType } from "@/lib/i18n/calendar";

export interface CalendarNavigation {
  /** 新增一筆安排（開建立頁，帶上那一天的日期）。 */
  openCreate: (date: string) => void;
  /** 新增一筆**週期**事件（`category=cycle` → 結束日自動展開）。 */
  openCycleEvent: (date: string) => void;
  /** 編輯一個事件（投影類別沒有 id，不會到這裡）。 */
  openEdit: (item: CalendarItem) => void;
  /** 前往事件的來源：自有事件 → 詳情頁；投影 → 來源模組。 */
  openItemSource: (item: CalendarItem) => void;
}

export function useCalendarNavigation(): CalendarNavigation {
  const nav = useNavigate();

  const openCreate = useCallback(
    (date: string) => {
      // W40.1 F7 — a route, not a sheet. The create form is the body of
      // `/calendar/events/new`, composed like the other six create pages, and
      // the day it was long-pressed on travels as `?date=` so the form opens
      // there rather than on today.
      nav(`/calendar/events/new?date=${encodeURIComponent(date)}`);
    },
    [nav],
  );

  // W58.5 — **「開一個週期事件」只有一條路**（週期頁的「經期來了」、
  // 月曆頁的「一鍵補記」、日詳情的「標記今日經期開始」都是它）。
  // `category=cycle` 讓表單的結束日自動依平均經期長度展開。
  const openCycleEvent = useCallback(
    (date: string) =>
      nav(`/calendar/events/new?date=${encodeURIComponent(date)}&category=cycle`),
    [nav],
  );

  const openEdit = useCallback(
    (item: CalendarItem) => {
      if (!item.id) return;
      nav(`/calendar/events/${item.id}/edit`);
    },
    [nav],
  );

  const openItemSource = useCallback(
    (item: CalendarItem) => {
      // Two destinations, and the split is the point of Q21.
      //
      // An OWNED event has no source record to jump back to, so it gets a real
      // detail page. This used to re-open the day sheet we were already in —
      // a control that rendered, took clicks, and changed nothing. Every other
      // content module ends in a route (`/diaries/:id`, `/wishes/:id`, …), so
      // the calendar was the odd one out.
      //
      // A PROJECTION has no id at all (Q7); its `source_type` + `source_id` are
      // the only way back to the module that owns it, which is the Q21
      // bidirectional entry.
      if (item.id) {
        nav(`/calendar/events/${item.id}`);
        return;
      }
      if (item.source_type && item.source_id && isLinkSourceType(item.source_type)) {
        nav(`${LINK_SOURCE_ROUTE[item.source_type]}${item.source_id}`);
      }
    },
    [nav],
  );

  return { openCreate, openCycleEvent, openEdit, openItemSource };
}
